import { zip } from 'rxjs/operators';
import { Component, OnInit, ChangeDetectorRef, ViewChild } from '@angular/core';
import { UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { Location } from '@angular/common';
import { Router, ActivatedRoute, Params } from '@angular/router';
import {
  PeopleService,
  LanguageService,
  ApplicationService,
  MachineFamillyService,
  LocalEntityService
} from '../../shared/services/index';
import { BaseModalPageComponent } from '../../base-modal.component';
import { ModalComponent } from '../../modal/modal.component';
import { ConfirmComponent } from '../../modal/confirm.component';
import { ProfilPipe, LocalEntityPipe } from '../../pipes/index';
import * as _ from 'lodash';
import { maxPhoneLengthValidator } from '../../utils/maxPhoneLength.directive';
import { Person } from '../../shared/models';
import { PeopleMenuComponent } from '../../menus/plugins/people.menu';
import { PermissionService } from '../../shared/services/permission.service';

@Component({
    templateUrl: './edit.component.html',
    standalone: false
})
export class EditPersonDetailPageComponent extends BaseModalPageComponent implements OnInit {
  public person: Person;
  public personId: string;
  editForm: UntypedFormGroup;
  public processing = false;
  public languages: any[];
  public applicationTypes: any[];
  @ViewChild('menuModal', { static: true })
  menuModal: ModalComponent;
  @ViewChild('deleteConfirm', { static: true })
  deleteConfirm: ConfirmComponent;
  @ViewChild('permissionsModal', { static: true })
  permissionsModal: ModalComponent;
  @ViewChild('productLinesModal', { static: true })
  productLinesModal: ModalComponent;
  @ViewChild('localEntitiesModal', { static: true })
  localEntitiesModal: ModalComponent;
  @ViewChild('officeModal', { static: true })
  officeModal: ModalComponent;
  public permissions: any;
  public productLines: any[];
  public localEntities: any;
  public currentApplicationType: number;
  public currentPerson: Person;

  constructor(
    location: Location,
    private activatedRoute: ActivatedRoute,
    private router: Router,
    private language: LanguageService,
    private peopleService: PeopleService,
    private applicationService: ApplicationService,
    private profilPipe: ProfilPipe,
    private localEntityPipe: LocalEntityPipe,
    private cdf: ChangeDetectorRef,
    private machineFamillyService: MachineFamillyService,
    private localEntityService: LocalEntityService,
    private permissionService: PermissionService
  ) {
    super(location);
  }

  ngOnInit(): void {
    const self = this;
    self.localEntityService
      .getLocalEntities()
      .pipe(zip(this.applicationService.getApplicationTypes()))
      .subscribe(([localEntities, types]) => {
        self.localEntities = _.orderBy(localEntities, ['label']);
        self.language.getLanguageList().subscribe((data) => (this.languages = data));
        self.applicationTypes = types;
        self.initRoute();
      });
  }

  initRoute(): void {
    this.activatedRoute.params.subscribe((params: Params) => {
      const id = params['personId'];
      if (id == null) this.cancel();

      this.personId = id;

      this.initData();
    });
  }

  public cancel() {
    this.goToBack();
  }

  save(model: any): void {
    this.processing = true;
    _.remove(model.permissions, (t: any) => !t.selected);
    _.remove(model.localEntities, (t: any) => !t.selected);
    model.permissions = _.map(model.permissions, (t: any) => t.value);
    model.localEntities = _.map(model.localEntities, (t: any) => t.value);

    this.peopleService
      .updatePerson(this.personId, {
        name: model.name,
        description: model.desc,
        phone: model.phone,
        email: model.email,
        language: model.language,
        applicationTypes: this.person.applicationTypes,
        permissions: model.permissions,
        localEntities: model.localEntities,
        productLine: model.productLine.filter((p) => p.selected).map((p) => p.id),
        office: model.office
      })
      .subscribe((person: any) => {
        this.processing = false;
        this.person = person;
        this.goToBack();
      });
  }

  initData(): void {
    this.permissionService.getPermissionsList().subscribe((permissions) => {
      this.permissions = _.orderBy(permissions, ['label']);

      this.peopleService
        .getPerson(this.personId)
        .pipe(
          zip(
            this.machineFamillyService.getProductLines(),
            this.peopleService.getCurrent(),
            this.applicationService.getCurrent()
          )
        )
        .subscribe(([data, productLines, currentPerson, currentApplicationType]) => {
          this.currentPerson = currentPerson;
          this.currentApplicationType = currentApplicationType;
          this.person = data;
          productLines.push(this.machineFamillyService.getAllProductLineItem());

          this.productLines = _.map(productLines, (p) => ({
            id: 'productline-' + p.id,
            name: p.name,
            label: p.name
          }));

          this.productLines = _.orderBy(this.productLines, ['name']);
          this.initForm();
          this.cdf.detectChanges();
        });
    });
  }

  isInPersonApplicationTypes(applicationType: number) {
    return this.person.applicationTypes && this.person.applicationTypes.indexOf(applicationType) !== -1;
  }

  updateApplicationTypes(applicationType: number) {
    if (!this.isInPersonApplicationTypes(applicationType)) {
      if (!this.person.applicationTypes) {
        this.person.applicationTypes = [];
      }
      this.person.applicationTypes.push(applicationType);
    } else {
      const index = this.person.applicationTypes ? this.person.applicationTypes.indexOf(applicationType) : -1;
      this.person.applicationTypes.splice(index, 1);
    }
    this.cdf.detectChanges();
  }

  public openProductLinesSelector(): void {
    this.openMultipleSelector(this.productLinesModal, this.editForm.controls['productLine'].value);
  }

  public selectProductLinesMenu(item: any) {}

  public onProductLinesMenuClose(e: any) {
    this.editForm.controls['productLine'].markAsTouched();
  }

  initForm() {
    const regex = new RegExp(/<br\s*[\/]?>/gi);

    this.editForm = new UntypedFormGroup({
      name: new UntypedFormControl(this.person != null ? this.person.name : '', Validators.required),
      desc: new UntypedFormControl(
        this.person != null && this.person.desc ? this.person.desc.replace(regex, '\n') : ''
      ),
      email: new UntypedFormControl(this.person != null ? this.person.email : ''),
      phone: new UntypedFormControl(this.person != null ? this.person.phone : '', [
        Validators.pattern(/^([+]?\d{1,2}[-| \s]?)?(\d{1,4}[-| ]?){1,4}?(\d{1,4}[-| ]?)\d{1,4}$/),
        Validators.minLength(10),
        maxPhoneLengthValidator(21)
      ]),
      language: new UntypedFormControl(this.person != null ? this.person.language : ''),
      permissions: new UntypedFormControl(
        this.person != null ? this.processPermissions(this.person.permissions) : this.processPermissions(null)
      ),
      productLine: new UntypedFormControl(
        this.person != null ? this.processProductLines(this.person.productLine) : this.processProductLines(null)
      ),
      localEntities: new UntypedFormControl(
        this.person != null ? this.processLocalEntities(this.person.localEntities) : this.processLocalEntities(null)
      ),
      office: new UntypedFormControl(this.person.office)
    });
  }

  processPermissions(permissions: any): any {
    const clonedPermissions = _.cloneDeep(this.permissions);
    _.each(permissions, (el) => {
      const i = _.findIndex(clonedPermissions, ['value', el]);
      if (i !== -1) {
        clonedPermissions[i].selected = true;
      }
    });
    return clonedPermissions;
  }

  processLocalEntities(localEntities: any): any {
    const clonedLocalEntities = _.cloneDeep(this.localEntities);
    _.each(localEntities, (el) => {
      const i = _.findIndex(clonedLocalEntities, ['value', el]);
      if (i !== -1) {
        clonedLocalEntities[i].selected = true;
      }
    });
    return clonedLocalEntities;
  }

  public getSelectedValues(): string {
    const permissions = this.editForm.controls['permissions'].value;
    const defaultValue = 'Select a permission';
    let selectedValues = _.filter(permissions, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.profilPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public getSelectedLocalEntitiesValues(): string {
    const localEntities = this.editForm.controls['localEntities'].value;
    const defaultValue = 'Select a local entity';
    let selectedValues = _.filter(localEntities, (item: any) => item.selected);
    selectedValues = _.map(selectedValues, (item: any) => this.localEntityPipe.transform(item.value));
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  processProductLines(productLines: any): string {
    const clonedProductLines = _.cloneDeep(this.productLines);
    _.each(productLines, (el) => {
      const i = _.findIndex(clonedProductLines, ['id', el]);
      if (i !== -1) {
        clonedProductLines[i].selected = true;
      }
    });
    return clonedProductLines;
  }

  public getSelectedProductLineValues(): string {
    const productLines = this.editForm.controls['productLine'].value;
    const defaultValue = 'Select a product line';
    let selectedValues = _.filter(productLines, (item: any) => item.selected);
    selectedValues = selectedValues.map((s) => s.name);
    return selectedValues.length > 0 ? selectedValues.join('<br/>') : defaultValue;
  }

  public openPermissionsSelector(): void {
    this.openMultipleSelector(this.permissionsModal, this.editForm.controls['permissions'].value);
  }

  public openLocalEntitiesSelector(): void {
    this.openMultipleSelector(this.localEntitiesModal, this.editForm.controls['localEntities'].value, true);
  }

  public selectPermissionsMenu(item: any) {}
  public selectLocalEntitiesMenu(item: any) {}

  public selectOfficeMenu(item: any) {
    const selectedIndex = this.editForm.controls['office'].value === item.selectedIndex ? '' : item.selectedIndex;
    this.editForm.controls['office'].setValue(selectedIndex);
  }

  public openOfficeSelector(): void {
    this.openSelector(this.officeModal, this.localEntities, this.editForm.controls['office'].value);
  }

  public openMenu(): void {
    this.menuModal.modalFooter = false;
    this.menuModal.modalHeader = false;
    this.menuModal.modalMessage = false;
    this.menuModal.open(PeopleMenuComponent);
  }

  public selectMenu(item: any) {
    switch (item) {
      case 1:
        this.openDeleteConfirm();
        break;
      case 10:
        this.gotoEditJsonPage();
        break;
      default:
    }
  }

  gotoEditJsonPage() {
    return this.router.navigate([`${this.appRoute}/people`, this.personId, 'editjson']);
  }

  openDeleteConfirm() {
    this.deleteConfirm.open();
  }

  deletePerson() {
    this.peopleService.deletePerson(this.personId).subscribe((result: any) => {
      this.goToBack();
    });
  }
}
